// DEPLOY-PROBE-bump

/**
 * Registration modal for full admin: 従業員は本社+現場の両テーブル、管理者は本社のみ.
 */

import React, { useEffect, useState } from 'react';
import { createPortal } from 'react-dom';
import './AddNewUserModal.css';
import { fileUploadService } from '../../../../services/FileUploadService';

/**
 * Stored as `employees.userType`.
 * One assigned construction site (from salary data) — persistence in a separate table comes later.
 */
export const OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN = '担当現場管理者';

export interface UnifiedRegisterFormData {
  empCode: string;
  name: string;
  officeUserType: string;
  password: string;
  passwordConfirm: string;
  /** Genba name from construction salary data; set when `officeUserType` is {@link OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN}. */
  assignedGenbaName?: string;
}

interface UnifiedRegisterModalProps {
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (data: UnifiedRegisterFormData) => Promise<{ success: boolean; message?: string }>;
  /** 現場管理者: 従業員のみ（本社+現場の二重登録） */
  variant?: 'full' | 'constructionAdmin';
}

const isFourDigit = (v: string) => /^\d{4}$/.test(v);

/** Office name (no half-width katakana) — used for all types (本社テーブル基準) */
const isJapaneseNameOffice = (value: string): boolean => {
  const re = /^[\u3040-\u309F\u30A0-\u30FF\u31F0-\u31FF\u4E00-\u9FFF\u3400-\u4DBF\u3000\u3005\u30F5\u30F6\u30FB\u30FC\s]+$/;
  return re.test(value);
};

const isPasswordValid = (value: string): boolean =>
  /^(?=.*[A-Z])(?=.*\d)[A-Za-z0-9]+$/.test(value);

export const UnifiedRegisterModal: React.FC<UnifiedRegisterModalProps> = ({
  isOpen,
  onClose,
  onSubmit,
  variant = 'full',
}) => {
  const [empCode, setEmpCode] = useState('');
  const [name, setName] = useState('');
  const [officeUserType, setOfficeUserType] = useState('従業員');
  const [password, setPassword] = useState('');
  const [passwordConfirm, setPasswordConfirm] = useState('');
  const [assignedGenbaName, setAssignedGenbaName] = useState('');
  const [availableGenbas, setAvailableGenbas] = useState<string[]>([]);
  const [genbasLoading, setGenbasLoading] = useState(false);
  const [genbasError, setGenbasError] = useState<string | null>(null);
  const [errors, setErrors] = useState<Record<string, string>>({});
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    if (isOpen) {
      setEmpCode('');
      setName('');
      setOfficeUserType('従業員');
      setPassword('');
      setPasswordConfirm('');
      setAssignedGenbaName('');
      setAvailableGenbas([]);
      setGenbasLoading(false);
      setGenbasError(null);
      setErrors({});
    }
  }, [isOpen, variant]);

  useEffect(() => {
    if (!isOpen || variant !== 'full' || officeUserType !== OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN) {
      return;
    }
    let cancelled = false;
    (async () => {
      setGenbasLoading(true);
      setGenbasError(null);
      try {
        const genbas = await fileUploadService.getConstructionAvailableGenbas();
        const list = (Array.isArray(genbas) ? genbas : [])
          .map((g) => String(g).trim())
          .filter(Boolean);
        const sorted = Array.from(new Set(list)).sort((a, b) => a.localeCompare(b, 'ja'));
        if (!cancelled) {
          setAvailableGenbas(sorted);
          setAssignedGenbaName((prev) => (prev && sorted.includes(prev) ? prev : ''));
        }
      } catch {
        if (!cancelled) {
          setAvailableGenbas([]);
          setGenbasError('現場一覧の取得に失敗しました。');
          setAssignedGenbaName('');
        }
      } finally {
        if (!cancelled) setGenbasLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, [isOpen, variant, officeUserType]);

  useEffect(() => {
    if (isOpen) {
      document.body.style.overflow = 'hidden';
      return () => {
        document.body.style.overflow = '';
      };
    }
    return undefined;
  }, [isOpen]);

  const validate = (): boolean => {
    const e: Record<string, string> = {};
    if (!empCode) e.empCode = '従業員コードを入力してください（4桁）';
    else if (!isFourDigit(empCode)) e.empCode = '従業員コードは4桁の数字で入力してください';

    if (!name) e.name = '名前は必須です';
    else if (!isJapaneseNameOffice(name)) {
      e.name = '名前は漢字・ひらがな・カタカナのみ使用できます';
    }

    if (!officeUserType) e.officeUserType = 'ユーザータイプを選択してください';

    if (officeUserType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN) {
      if (genbasLoading) e.assignedGenba = '現場一覧を読み込み中です。しばらくお待ちください。';
      else if (genbasError) e.assignedGenba = genbasError;
      else if (availableGenbas.length === 0) {
        e.assignedGenba = '建設給与データに現場がありません。先にデータを登録してください。';
      } else if (!assignedGenbaName.trim()) {
        e.assignedGenba = '担当現場を選択してください';
      }
    }

    if (!password) e.password = 'パスワードは必須です';
    else if (password.length < 6) e.password = 'パスワードは6文字以上である必要があります';
    else if (!isPasswordValid(password))
      e.password = 'パスワードは英数字のみで、少なくとも1つの大文字と数字を含めてください';
    if (password !== passwordConfirm) e.passwordConfirm = 'パスワードが一致しません';

    setErrors(e);
    return Object.keys(e).length === 0;
  };

  const handleSubmit = async (ev: React.FormEvent) => {
    ev.preventDefault();
    if (!validate()) return;
    setSubmitting(true);
    try {
      const result = await onSubmit({
        empCode,
        name,
        officeUserType,
        password,
        passwordConfirm,
        assignedGenbaName:
          officeUserType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN ? assignedGenbaName.trim() : undefined,
      });
      if (result.success) {
        onClose();
      } else if (result.message) {
        const msg = result.message;
        if (
          msg.includes('同じ人を二重に登録')
          || msg.includes('この名前の方は既に登録')
          || msg.toLowerCase().includes('name already exists')
        ) {
          setErrors({ name: msg });
        } else if (
          msg.includes('従業員コードは既に')
          || msg.includes('既に使用されています')
          || msg.toLowerCase().includes('empcode')
          || msg.toLowerCase().includes('emp code')
        ) {
          setErrors({ empCode: msg });
        } else {
          setErrors({ _form: msg });
        }
      }
    } finally {
      setSubmitting(false);
    }
  };

  if (!isOpen) return null;

  return createPortal(
    <div
      className="modal-overlay"
      style={{ display: 'flex', position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, zIndex: 10000 }}
      onClick={(e) => e.target === e.currentTarget && !submitting && onClose()}
    >
      <div className="modal-content" onClick={(e) => e.stopPropagation()}>
        <div className="modal-header">
          <h3>新規登録</h3>
          <button type="button" className="close-button" onClick={() => !submitting && onClose()} disabled={submitting}>
            ✕
          </button>
        </div>

        <form onSubmit={handleSubmit} className="add-user-form">
          <p className="add-user-field-hint" style={{ marginTop: 0 }}>
            {variant === 'constructionAdmin'
              ? '従業員として本社・現場の両方に登録されます。'
              : '従業員は本社・現場の両方に登録されます。管理者は本社のみ登録されます。'}
          </p>

          <div className="form-group">
            <label htmlFor="reg-emp">従業員コード *</label>
            <input
              id="reg-emp"
              type="text"
              inputMode="numeric"
              maxLength={4}
              value={empCode}
              onChange={(e) => {
                const v = e.target.value.replace(/\D/g, '').slice(0, 4);
                setEmpCode(v);
              }}
              placeholder="4桁の数字"
              className={errors.empCode ? 'error' : ''}
              disabled={submitting}
            />
            {errors.empCode && <span className="error-message">{errors.empCode}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="reg-name">名前 *</label>
            <input
              id="reg-name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="フルネーム"
              className={errors.name ? 'error' : ''}
              disabled={submitting}
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>

          {variant === 'full' && (
            <div className="form-group">
              <label htmlFor="reg-ut">ユーザータイプ *</label>
              <select
                id="reg-ut"
                value={officeUserType}
                onChange={(e) => setOfficeUserType(e.target.value)}
                disabled={submitting}
                className={errors.officeUserType ? 'error' : ''}
              >
                <option value="従業員">従業員</option>
                <option value="管理者">管理者（上位権限）</option>
                <option value="現場管理者">管理者（現場権限）</option>
                <option value={OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN}>管理者（担当現場権限）</option>
              </select>
              {errors.officeUserType && <span className="error-message">{errors.officeUserType}</span>}
            </div>
          )}

          {variant === 'full' && officeUserType === OFFICE_USER_TYPE_ASSIGNED_SITE_ADMIN && (
            <div className="form-group">
              <label htmlFor="reg-assigned-genba">担当現場 *</label>
              <select
                id="reg-assigned-genba"
                value={assignedGenbaName}
                onChange={(e) => setAssignedGenbaName(e.target.value)}
                disabled={submitting || genbasLoading || availableGenbas.length === 0}
                className={errors.assignedGenba ? 'error' : ''}
              >
                <option value="">現場を選択</option>
                {availableGenbas.map((g) => (
                  <option key={g} value={g}>
                    {g}
                  </option>
                ))}
              </select>
              {genbasLoading && <span className="add-user-field-hint">現場一覧を読み込み中…</span>}
              {errors.assignedGenba && <span className="error-message">{errors.assignedGenba}</span>}
            </div>
          )}

          <div className="form-group">
            <label htmlFor="reg-pw">パスワード *</label>
            <input
              id="reg-pw"
              type="password"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              className={errors.password ? 'error' : ''}
              disabled={submitting}
            />
            {errors.password && <span className="error-message">{errors.password}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="reg-pw2">パスワード再入力 *</label>
            <input
              id="reg-pw2"
              type="password"
              value={passwordConfirm}
              onChange={(e) => setPasswordConfirm(e.target.value)}
              className={errors.passwordConfirm ? 'error' : ''}
              disabled={submitting}
            />
            {errors.passwordConfirm && <span className="error-message">{errors.passwordConfirm}</span>}
          </div>

          {errors._form && (
            <p className="error-message" role="alert">
              {errors._form}
            </p>
          )}

          <div className="form-actions">
            <button type="button" className="btn btn-secondary" onClick={() => !submitting && onClose()} disabled={submitting}>
              キャンセル
            </button>
            <button type="submit" className="btn btn-primary" disabled={submitting}>
              {submitting ? '登録中...' : '登録'}
            </button>
          </div>
        </form>
      </div>
    </div>,
    document.body
  );
};
